import { getLexicalTextLength } from "@facade/components/ui";
import z from "zod/v3";

export const MAX_ISSUE_TITLE_LENGTH = 120;
export const MAX_ISSUE_DESCRIPTION_LENGTH = 1000;
export const MAX_ISSUE_CATEGORY_LENGTH = 80;
export const MAX_ISSUE_TAG_LENGTH = 40;
export const MAX_ISSUE_TAGS = 12;

export const MAX_ISSUE_TASK_DESCRIPTION_LENGTH = 350;
export const MAX_ISSUE_TASKS = 10;

export const issuePrioritySchema = z.enum([
  "LOW",
  "MODERATE",
  "HIGH",
  "CRITICAL",
]);

export const issueStatusSchema = z.enum([
  "OPEN",
  "CHECKING",
  "IN_PROGRESS",
  "CLOSED",
  "ARCHIVED",
]);

export const issueAttachmentTypeSchema = z.enum([
  "IMAGE",
  "BIM_VIEWPOINT",
  "DOCUMENT",
]);

export const issueLocationSchema = z
  .object({
    zoneId: z.string().uuid().optional().nullable(),
    storeyId: z.string().uuid().optional().nullable(),
    areaId: z.string().uuid().optional().nullable(),
    elementId: z.string().trim().optional().nullable(),
  })
  .superRefine((location, ctx) => {
    if (location.areaId && !location.storeyId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["storeyId"],
        message: "Storey is required when area is selected",
      });
    }

    if (location.storeyId && !location.zoneId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["zoneId"],
        message: "Zone is required when storey is selected",
      });
    }
  });

export const imageAttachmentMetadataSchema = z.object({
  kind: z.literal("IMAGE"),
  fileName: z.string().optional(),
  contentType: z.string().optional(),
  size: z.number().optional().nullable(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
});

export const issueImageAttachmentSchema = z.object({
  attachmentId: z.string().trim().min(1),
  type: z.literal("IMAGE"),
  caption: z.string().optional().nullable(),
  metadata: imageAttachmentMetadataSchema.optional().nullable(),
});

const issueDocumentAttachmentSchema = z.object({
  type: z.literal("DOCUMENT"),
  attachmentId: z.string(),
  caption: z.string().nullable().optional(),

  metadata: z
    .object({
      kind: z.literal("DOCUMENT"),
      fileName: z.string().optional(),
      contentType: z.string().optional(),
      size: z.number().nullable().optional(),
    })
    .optional(),
});

const vector3Schema = z.tuple([z.number(), z.number(), z.number()]);

const viewerToolStateSchema = z
  .object({
    version: z.literal(1),
    pin: z
      .object({
        position: vector3Schema,
        normal: vector3Schema,
      })
      .nullable()
      .optional(),
    cut: z
      .object({
        mode: z.enum(["vertical", "horizontal"]),
        position: vector3Schema,
        normal: vector3Schema,
      })
      .nullable()
      .optional(),
  })
  .passthrough();

const issueBimViewpointAttachmentSchema = z.object({
  type: z.literal("BIM_VIEWPOINT"),
  attachmentId: z.string(),
  caption: z.string().nullable().optional(),
  metadata: z.object({
    kind: z.literal("BIM_VIEWPOINT"),
    camera: z.object({
      position: vector3Schema,
      target: vector3Schema,
      up: vector3Schema.nullable().optional(),
      projection: z.enum(["PERSPECTIVE", "ORTHOGRAPHIC"]),
      zoom: z.number().nullable().optional(),
    }),
    selectedElementIds: z.array(z.string()),
    hiddenElementIds: z.array(z.string()),
    isolatedElementIds: z.array(z.string()),
    viewerState: viewerToolStateSchema.nullable().optional(),
  }),
});

export const issueAttachmentSchema = z.discriminatedUnion("type", [
  issueImageAttachmentSchema,
  issueBimViewpointAttachmentSchema,
  issueDocumentAttachmentSchema,
]);

export const issueTaskStatusSchema = z.enum(["OPEN", "DONE"]);

export const issueTaskSchema = z.object({
  id: z.string().uuid().optional(),

  description: z
    .string()
    .trim()
    .min(1, "Task description is required")
    .max(
      MAX_ISSUE_TASK_DESCRIPTION_LENGTH,
      `Task description cannot exceed ${MAX_ISSUE_TASK_DESCRIPTION_LENGTH} characters`,
    ),

  assigneeId: z.string().optional().nullable(),

  disciplineId: z
    .string()
    .uuid("Selecione uma disciplina válida")
    .optional()
    .nullable(),

  status: issueTaskStatusSchema,
});

export const createIssueSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Issue title is required")
    .max(
      MAX_ISSUE_TITLE_LENGTH,
      `Issue title cannot exceed ${MAX_ISSUE_TITLE_LENGTH} characters`,
    ),

  priority: issuePrioritySchema,
  status: issueStatusSchema,

  locations: z.array(issueLocationSchema),

  disciplineIds: z.array(z.string()),

  phaseId: z.string().uuid("Selecione uma fase válida").optional().nullable(),

  categoryId: z
    .string()
    .uuid("Selecione uma categoria válida")
    .optional()
    .nullable(),

  description: z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (value) => getLexicalTextLength(value) <= MAX_ISSUE_DESCRIPTION_LENGTH,
      {
        message: `A descrição não pode exceder ${MAX_ISSUE_DESCRIPTION_LENGTH} caracteres`,
      },
    ),

  dueDate: z.string().datetime().optional().nullable(),

  assigneeIds: z.array(z.string().uuid()),

  tags: z
    .array(
      z
        .string()
        .trim()
        .min(1)
        .max(
          MAX_ISSUE_TAG_LENGTH,
          `Tag cannot exceed ${MAX_ISSUE_TAG_LENGTH} characters`,
        ),
    )
    .max(MAX_ISSUE_TAGS, `You can add up to ${MAX_ISSUE_TAGS} tags`),

  attachments: z.array(issueAttachmentSchema),

  tasks: z
    .array(issueTaskSchema)
    .max(MAX_ISSUE_TASKS, `You can add up to ${MAX_ISSUE_TASKS} tasks`),
});

const draftIssueTaskSchema = issueTaskSchema.extend({
  description: z
    .string()
    .trim()
    .max(
      MAX_ISSUE_TASK_DESCRIPTION_LENGTH,
      `Task description cannot exceed ${MAX_ISSUE_TASK_DESCRIPTION_LENGTH} characters`,
    ),
});

export const draftIssueSchema = createIssueSchema.extend({
  title: z
    .string()
    .trim()
    .max(
      MAX_ISSUE_TITLE_LENGTH,
      `Issue title cannot exceed ${MAX_ISSUE_TITLE_LENGTH} characters`,
    ),
  tasks: z
    .array(draftIssueTaskSchema)
    .max(MAX_ISSUE_TASKS, `You can add up to ${MAX_ISSUE_TASKS} tasks`),
});

export type CreateIssueFormData = z.infer<typeof createIssueSchema>;
